/**
 * Data Dictionary (Insights Lab) — what every serve table is, in English,
 * and what each of its columns means, under the names the Lab's queries
 * use. From GET /api/v1/serve/dictionary (serve/columns.py on the server:
 * the same definitions rename the columns, so the page cannot drift from
 * what a query returns).
 */
import { useEffect, useMemo, useState } from 'react'

import { api, type Session } from '../lib/api'
import './data-dictionary.css'

export type DictColumn = { name: string; type: string; description: string }
export type DictTable = {
  table: string; summary: string; grain: string; layer: string
  columns: DictColumn[]
}
export type Dictionary = {
  conventions: string
  context: { name: string; description: string }[]
  roles: { prefix: string; who: string }[]
  tables: DictTable[]
}

/** Tables and columns matching `q` (name or words, case-insensitive): a
 *  table matching by name or summary keeps every column; otherwise only
 *  its matching columns. */
export function filterDictionary(tables: DictTable[], q: string): DictTable[] {
  const s = q.trim().toLowerCase()
  if (!s) return tables
  const has = (x: string) => x.toLowerCase().includes(s)
  const out: DictTable[] = []
  for (const t of tables) {
    if (has(t.table) || has(t.summary)) {
      out.push(t)
      continue
    }
    const cols = t.columns.filter((c) => has(c.name) || has(c.description))
    if (cols.length) out.push({ ...t, columns: cols })
  }
  return out
}

const LAYER_NAMES: Record<string, string> = {
  l3: 'Tracking', l5: 'Team structure', l6: 'Analysis', events: 'Events',
  summaries: 'Summaries',
}

export default function DataDictionary({ session: _session }: { session: Session }) {
  return <DataDictionaryView />
}

/** The dictionary itself: the Insights Lab tab and the Ops/QA Docs page
 *  render this one component from the one endpoint. */
export function DataDictionaryView() {
  const [dict, setDict] = useState<Dictionary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    api<Dictionary>('/api/v1/serve/dictionary')
      .then((d) => { if (alive) setDict(d) })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : 'Could not load the dictionary.')
      })
    return () => { alive = false }
  }, [])

  const tables = useMemo(
    () => (dict ? filterDictionary(dict.tables, q) : []), [dict, q])

  if (error) return <p className="dd-error" role="alert">{error}</p>
  if (!dict) return <p className="lab-sub">Loading the data dictionary&hellip;</p>

  return (
    <div className="dd">
      <div className="dd-bar">
        <input type="search" placeholder="Search tables and columns"
               aria-label="Search tables and columns" value={q}
               onChange={(e) => setQ(e.target.value)} />
        <span className="dd-muted">
          {tables.length} of {dict.tables.length} tables
        </span>
      </div>

      <div className="dd-layout">
        <nav className="dd-nav" aria-label="Tables">
          {tables.map((t) => (
            <a key={t.table} href={`#dd-${t.table}`}>{t.table}</a>
          ))}
        </nav>

        <div className="dd-body">
          {!q && (
            <section className="dd-card">
              <h3>Reading the tables</h3>
              <p className="lab-sub">{dict.conventions}</p>
              <h4>Columns added to most tables</h4>
              <p className="dd-muted">
                Where they apply, tables carry the row&rsquo;s team, player and
                time, so most questions need no joins.
              </p>
              <table className="dd-cols">
                <tbody>
                  {dict.context.map((c) => (
                    <tr key={c.name}>
                      <th scope="row"><code>{c.name}</code></th>
                      <td>{c.description}</td>
                    </tr>
                  ))}
                  <tr>
                    <th scope="row"><code>receiver_player_name</code>&hellip;</th>
                    <td>
                      Other players on a row get the same columns with a prefix:{' '}
                      {dict.roles.map((r) => `${r.prefix} (${r.who.toLowerCase()})`).join(', ')}.
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {tables.map((t) => (
            <section key={t.table} id={`dd-${t.table}`} className="dd-card"
                     data-testid={`dd-table-${t.table}`}>
              <header className="dd-head">
                <h3><code>{t.table}</code></h3>
                <span className="dd-tag">{LAYER_NAMES[t.layer] ?? t.layer}</span>
              </header>
              <p className="dd-summary">{t.summary}</p>
              {t.grain && <p className="dd-muted">One row per {t.grain}.</p>}
              <table className="dd-cols">
                <thead>
                  <tr><th scope="col">Column</th><th scope="col">Type</th><th scope="col">Meaning</th></tr>
                </thead>
                <tbody>
                  {t.columns.map((c) => (
                    <tr key={c.name}>
                      <th scope="row"><code>{c.name}</code></th>
                      <td className="dd-type">{c.type}</td>
                      <td>{c.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
          {tables.length === 0 && <p className="dd-muted">Nothing matches &ldquo;{q}&rdquo;.</p>}
        </div>
      </div>
    </div>
  )
}
